import React from "react";
import BrowzwearConnectCard from "pages/Account/components/BrowzwearConnectCard";
import MiroConnectionCard from "pages/Account/components/MiroConnectionCard";
import { useAppSelector } from "store";
import BrowzwearIntegration from "./Integrations";
import IntegrationCard from "./IntegrationCard";
import MiroIntegration from "./MiroIntegration";
import useBrowzwearCardProps from "./useBrowzwearCardProps";

interface IntegrationsHubProps {
  /** `full` = the sidebar /integrations page (each integration's card + its extras); `compact` = Account → Connected Apps (connection cards only). */
  mode: "full" | "compact";
}

/** 2 columns from `clg` (≥1024px), 1 below; every integration is a peer card in the same grid. `items-start`: each card is as tall as its own content
 * (a tall Browzwear form must not stretch the Miro card into an empty gap above its footer). */
const GRID = "grid grid-cols-1 items-start gap-4 clg:grid-cols-2";

// The Browzwear pieces keep their own markup; these only neutralise the outer spacing/width/frame they were built with
// (a centred, margin-topped gate form) so they sit INSIDE the shared card instead of framing themselves twice.
const BROWZWEAR_PAGE_PIECE = "[&>div]:mx-0 [&>div]:mt-0 [&>div]:max-w-none [&>div]:p-0 [&>div]:!bg-transparent";
const BROWZWEAR_CARD_WIDTH = "[&>div]:max-w-none";

/**
 * The one Integrations component (REF-6702), shown in Account → Connected Apps and on /integrations.
 *
 * Browzwear is NOT re-implemented here: each mode renders the Browzwear piece that placement always had, unchanged —
 * `BrowzwearConnectCard` (compact, moved verbatim from Account) / the existing page `Integrations` (full: gate form + asset browser),
 * which the full mode only places inside the shared card frame and grid slot.
 */
const IntegrationsHub = ({ mode }: IntegrationsHubProps) => {
  const browzwearCardProps = useBrowzwearCardProps();
  const { browzwear } = useAppSelector((state) => state.common);

  if (mode === "full") {
    return (
      <div className={GRID} data-testid="integrations-grid">
        <MiroIntegration />
        {/* Connected, the board browser is a wide tile grid: it takes the full row instead of half of it. */}
        <div className={browzwear.isConnected ? "min-w-0 clg:col-span-2" : "min-w-0"}>
          <IntegrationCard data-testid="browzwear-card" {...browzwearCardProps} bodyClassName={BROWZWEAR_PAGE_PIECE}>
            <BrowzwearIntegration />
          </IntegrationCard>
        </div>
      </div>
    );
  }

  return (
    <div className={GRID}>
      {/* Same order as the full mode above: Miro first. */}
      <MiroConnectionCard />
      <div className={BROWZWEAR_CARD_WIDTH}>
        <BrowzwearConnectCard />
      </div>
    </div>
  );
};

export default IntegrationsHub;
